import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, ExternalLink, Plus, X } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { useAdminT } from "./adminI18n";
import { deriveFromAccent, deriveFromBg } from "../../colorUtils";
import { ImageField } from "./ImageField";

const LIGHT_DEFAULTS = { light_bg: "#F8F6F0", light_text: "#1B0E23", light_muted: "#6A5872", light_border: "#E5DCD0" };

export const PALETTES = [
  { id: "royal", name: "أوبيرجين ملكي", colors: { bg: "#1B0E23", bg_alt: "#351F44", surface: "#281534", accent: "#D1A05C", accent_light: "#F3D08C", text: "#F3ECE2", muted: "#BBA8BF", ...LIGHT_DEFAULTS } },
  { id: "rose", name: "وردي ليلي", colors: { bg: "#190A12", bg_alt: "#10050B", surface: "#281522", accent: "#D990AC", accent_light: "#F6D9E3", text: "#FBF3F6", muted: "#C5A3B4", ...LIGHT_DEFAULTS } },
  { id: "emerald", name: "زمردي فاخر", colors: { bg: "#081A13", bg_alt: "#04100B", surface: "#102A20", accent: "#CBA35C", accent_light: "#EFDCAE", text: "#F1F7F3", muted: "#9FBBAC", ...LIGHT_DEFAULTS } },
  { id: "navy", name: "كحلي عميق", colors: { bg: "#0A1024", bg_alt: "#060918", surface: "#131C38", accent: "#C9A45C", accent_light: "#EBD9A8", text: "#F1F4FB", muted: "#9CABCE", ...LIGHT_DEFAULTS } },
  { id: "onyx", name: "أسود ذهبي", colors: { bg: "#0B0B0D", bg_alt: "#060607", surface: "#17171B", accent: "#D4AF37", accent_light: "#F0D98C", text: "#F6F5F3", muted: "#ABA79F", ...LIGHT_DEFAULTS } },
];

const FONT_PAIRS = [
  { id: "amiri", name: "الأميرية الكلاسيكية", serif: "Amiri", sans: "Tajawal" },
  { id: "naskh", name: "النسخية الدافئة", serif: "Noto Naskh Arabic", sans: "Tajawal" },
  { id: "messiri", name: "الحديثة الراقية", serif: "El Messiri", sans: "Cairo" },
  { id: "ruqaa", name: "الرقعة الفنية", serif: "Aref Ruqaa", sans: "Almarai" },
];

// Every color on the page, plainly labeled with exactly where it appears
const COLOR_FIELDS = [
  { key: "bg", label: "الخلفية الرئيسية الداكنة", where: "خلفية بداية الصفحة، قسم الاستشارات، الأسئلة الشائعة، التواصل، والفوتر" },
  { key: "bg_alt", label: "خلفية الأقسام البديلة", where: "خلفية قسم الدورات وقسم المنهجية" },
  { key: "surface", label: "لون البطاقات", where: "بطاقات الدورات، باقات الاستشارات، وبطاقات الأسئلة" },
  { key: "accent", label: "الذهبي الرئيسي", where: "الأزرار، الأسعار، أرقام خطوات المنهجية، والعناوين المميزة" },
  { key: "accent_light", label: "الذهبي الفاتح", where: "لمعان العناوين الذهبية المتدرجة" },
  { key: "text", label: "لون النص الأساسي", where: "العناوين والنصوص فوق الخلفيات الداكنة" },
  { key: "muted", label: "لون النص الثانوي", where: "الفقرات والتفاصيل الصغيرة فوق الخلفيات الداكنة" },
  { key: "mauve", label: "لون الموف الخافت", where: "لمسات هادئة جداً في الخلفيات والفواصل" },
  { key: "light_bg", label: "خلفية الأقسام الفاتحة", where: "قسم «من هي مايا» وقسم «قصص العميلات»" },
  { key: "light_text", label: "نص الأقسام الفاتحة", where: "العناوين والنصوص داخل الأقسام الفاتحة" },
  { key: "light_muted", label: "النص الثانوي للأقسام الفاتحة", where: "الفقرات داخل الأقسام الفاتحة" },
  { key: "light_border", label: "حدود بطاقات الأقسام الفاتحة", where: "إطارات البطاقات البيضاء داخل الأقسام الفاتحة" },
];

// Every image slot on the page
const IMAGE_SLOTS = [
  { key: "hero_bg", label: "صورة الواجهة الرئيسية", hint: "الصورة الكبيرة خلف العنوان في أعلى الصفحة" },
  { key: "portrait_url", label: "الصورة الشخصية العامة", hint: "تظهر في الشارة الدائرية بقسم الختام وفي الأماكن الاحتياطية" },
  { key: "about_image", label: "صورة قسم «من هي مايا»", hint: "الصورة داخل القسم الفاتح" },
  { key: "courses_image", label: "صورة ترويسة قسم الدورات", hint: "الصورة بجانب عنوان قسم البرامج والدورات" },
];

// Optional decorative background image for any section
const SECTION_BG_SLOTS = [
  { id: "courses", label: "قسم الدورات" },
  { id: "journey", label: "قسم المنهجية (خطوات التحول)" },
  { id: "about", label: "قسم من هي مايا" },
  { id: "testimonials", label: "قسم قصص العميلات" },
  { id: "consultations", label: "قسم الاستشارات الخاصة" },
  { id: "faq", label: "قسم الأسئلة الشائعة" },
  { id: "contact", label: "قسم التواصل" },
  { id: "cta", label: "شريط الختام (الدعوة الأخيرة)" },
];

export const AppearanceTab = () => {
  const { t } = useAdminT();
  const [palette, setPalette] = useState("royal");
  const [colors, setColors] = useState(PALETTES[0].colors);
  const [fonts, setFonts] = useState(FONT_PAIRS[0]);
  const [images, setImages] = useState({ hero_bg: "", portrait_url: "", about_image: "", courses_image: "" });
  const [consultImage, setConsultImage] = useState("");
  const [consultFull, setConsultFull] = useState(null);
  const [sectionBg, setSectionBg] = useState({});
  const [customPalettes, setCustomPalettes] = useState([]);
  const [paletteName, setPaletteName] = useState("");
  const [bgTexture, setBgTexture] = useState("standard");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load all preview fonts once so the samples render in their real typeface
    const fams = [...new Set(FONT_PAIRS.flatMap((p) => [p.serif, p.sans]))]
      .map((f) => `family=${f.replace(/ /g, "+")}:wght@300;400;600;700`)
      .join("&");
    if (!document.getElementById("themaya-font-preview")) {
      const link = document.createElement("link");
      link.id = "themaya-font-preview";
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?${fams}&display=swap`;
      document.head.appendChild(link);
    }
    axios
      .get(`${API}/settings`)
      .then((res) => {
        const d = res.data;
        if (d.colors) setColors({ ...PALETTES[0].colors, ...LIGHT_DEFAULTS, ...d.colors });
        if (d.theme_palette) setPalette(d.theme_palette);
        setBgTexture(d.bg_texture || "standard");
        if (d.fonts?.serif) {
          const match = FONT_PAIRS.find((p) => p.serif === d.fonts.serif && p.sans === d.fonts.sans);
          setFonts(match || { id: "custom", name: "مخصص", ...d.fonts });
        }
        setImages({
          hero_bg: d.hero_bg || "",
          portrait_url: d.portrait_url || "",
          about_image: d.about_image || "",
          courses_image: d.courses_image || "",
        });
        setConsultFull(d.consultation || null);
        setConsultImage(d.consultation?.image || "");
        setSectionBg(d.section_bg || {});
        setCustomPalettes(d.custom_palettes || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const applyPalette = (p) => {
    setPalette(p.id);
    setColors(p.colors);
  };

  const setAccent = (v) => {
    setPalette("custom");
    setColors((c) => ({ ...c, ...deriveFromAccent(v) }));
  };

  const setBg = (v) => {
    setPalette("custom");
    setColors((c) => ({ ...c, ...deriveFromBg(v) }));
  };

  const setColor = (key, v) => {
    setPalette("custom");
    setColors((c) => ({ ...c, [key]: v }));
  };

  const saveCurrentAsPalette = () => {
    const name = paletteName.trim();
    if (!name) { toast.error(t("سمّي اللوحة أولاً")); return; }
    const p = { id: `custom-${Date.now().toString(36)}`, name, colors: { ...colors } };
    setCustomPalettes((cur) => [...cur, p]);
    setPalette(p.id);
    setPaletteName("");
    toast.success(t("حُفظت اللوحة — اضغطي «حفظ المظهر» لتثبيتها"));
  };

  const deleteCustomPalette = (id) => {
    setCustomPalettes((cur) => cur.filter((p) => p.id !== id));
    if (palette === id) setPalette("custom");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        colors,
        theme_palette: palette,
        bg_texture: bgTexture,
        fonts: { serif: fonts.serif, sans: fonts.sans },
        hero_bg: images.hero_bg,
        portrait_url: images.portrait_url,
        about_image: images.about_image,
        courses_image: images.courses_image,
        section_bg: sectionBg,
        custom_palettes: customPalettes,
      };
      if (consultFull) payload.consultation = { ...consultFull, image: consultImage };
      await axios.put(`${API}/admin/settings`, payload, { headers: authHeaders() });
      toast.success(t("تم حفظ المظهر — افتحي الموقع لرؤية التغييرات"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-luxury-gold animate-spin" /></div>;
  }

  return (
    <div data-testid="admin-appearance-tab">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <h2 className="font-serif text-3xl text-luxury-bone mb-2">{t("المظهر")}</h2>
          <p className="text-luxury-gray text-sm">{t("تحكم كامل بألوان وصور الصفحة — كل شيء موضح بمكانه")}</p>
        </div>
        <a
          data-testid="appearance-open-site"
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-luxury-gold/60 text-luxury-gold px-5 py-2.5 rounded-full text-sm hover:bg-luxury-gold hover:text-luxury-black transition-colors"
        >
          <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
          {t("فتح الموقع لمعاينة التغييرات")}
        </a>
      </div>

      <section className="bg-luxury-surface border border-luxury-border p-8 mb-8">
        <h3 className="text-luxury-gold text-lg font-semibold mb-6">{t("لوحات فاخرة جاهزة")}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {PALETTES.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`palette-${p.id}`}
              onClick={() => applyPalette(p)}
              className={`relative rounded-2xl border p-4 text-start transition-all ${palette === p.id ? "border-luxury-gold ring-1 ring-luxury-gold" : "border-luxury-border hover:border-luxury-gold/50"}`}
              style={{ background: p.colors.bg }}
            >
              {palette === p.id && (
                <span className="absolute top-3 end-3 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: p.colors.accent }}>
                  <Check className="w-4 h-4" style={{ color: p.colors.bg }} strokeWidth={3} />
                </span>
              )}
              <div className="flex gap-1.5 mb-3">
                {[p.colors.bg, p.colors.surface, p.colors.accent, p.colors.accent_light].map((c, i) => (
                  <span key={i} className="w-6 h-6 rounded-full border border-white/10" style={{ background: c }} />
                ))}
              </div>
              <p className="text-sm font-semibold" style={{ color: p.colors.text }}>{t(p.name)}</p>
            </button>
          ))}
          {customPalettes.map((p) => (
            <div key={p.id} className="relative">
              <button
                type="button"
                data-testid={`palette-${p.id}`}
                onClick={() => applyPalette(p)}
                className={`w-full relative rounded-2xl border p-4 text-start transition-all ${palette === p.id ? "border-luxury-gold ring-1 ring-luxury-gold" : "border-luxury-border hover:border-luxury-gold/50"}`}
                style={{ background: p.colors.bg }}
              >
                {palette === p.id && (
                  <span className="absolute top-3 end-3 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: p.colors.accent }}>
                    <Check className="w-4 h-4" style={{ color: p.colors.bg }} strokeWidth={3} />
                  </span>
                )}
                <div className="flex gap-1.5 mb-3">
                  {[p.colors.bg, p.colors.surface, p.colors.accent, p.colors.accent_light].map((c, i) => (
                    <span key={i} className="w-6 h-6 rounded-full border border-white/10" style={{ background: c }} />
                  ))}
                </div>
                <p className="text-sm font-semibold" style={{ color: p.colors.text }}>{p.name}</p>
              </button>
              <button
                type="button"
                data-testid={`palette-delete-${p.id}`}
                onClick={() => deleteCustomPalette(p.id)}
                aria-label={t("حذف اللوحة")}
                className="absolute -top-2 -start-2 w-6 h-6 rounded-full bg-red-500/90 text-white flex items-center justify-center hover:bg-red-400 transition-colors z-10"
              >
                <X className="w-3.5 h-3.5" strokeWidth={2.5} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 mt-5 items-center">
          <input
            type="text"
            data-testid="palette-name-input"
            value={paletteName}
            onChange={(e) => setPaletteName(e.target.value)}
            placeholder={t("اسم لوحتك الجديدة…")}
            className="bg-luxury-black border border-luxury-border text-luxury-bone px-4 py-2.5 rounded-full text-sm focus:outline-none focus:border-luxury-gold w-56"
          />
          <button
            type="button"
            data-testid="palette-save-current"
            onClick={saveCurrentAsPalette}
            className="flex items-center gap-2 border border-luxury-gold/60 text-luxury-gold px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-luxury-gold hover:text-luxury-black transition-colors"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} />
            {t("حفظ الألوان الحالية كلوحة جديدة")}
          </button>
        </div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 mb-8" data-testid="bg-texture-section">
        <h3 className="text-luxury-gold text-lg font-semibold mb-6">{t("نسيج الخلفية")}</h3>
        <div className="grid grid-cols-2 gap-4 max-w-xl">
          {[
            { id: "standard", label: "قياسي / مسطّح" },
            { id: "velvet", label: "مخمل ناعم" },
          ].map((o) => (
            <button
              key={o.id}
              type="button"
              data-testid={`bg-texture-${o.id}`}
              onClick={() => setBgTexture(o.id)}
              className={`relative rounded-2xl border p-4 text-start transition-all ${bgTexture === o.id ? "border-luxury-gold ring-1 ring-luxury-gold" : "border-luxury-border hover:border-luxury-gold/50"}`}
              style={{ background: colors.bg }}
            >
              {bgTexture === o.id && (
                <span className="absolute top-3 end-3 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: colors.accent }}>
                  <Check className="w-4 h-4" style={{ color: colors.bg }} strokeWidth={3} />
                </span>
              )}
              <p className="text-sm font-semibold" style={{ color: colors.text }}>{t(o.label)}</p>
            </button>
          ))}
        </div>
        <p className="text-luxury-gray/70 text-xs mt-4">{t("«مخمل ناعم» يضيف عمقاً حريرياً خفيفاً جداً لخلفية الموقع — اللون الأساسي يبقى كما هو تماماً.")}</p>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 mb-8">
        <h3 className="text-luxury-gold text-lg font-semibold mb-2">{t("تعديل سريع")}</h3>
        <p className="text-luxury-gray text-xs mb-6">{t("غيّري هذين اللونين وتُشتق بقية الدرجات الداكنة تلقائياً لتحافظ على فخامة التصميم")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center gap-4 bg-luxury-black border border-luxury-border p-4 rounded-xl">
            <input type="color" data-testid="appearance-accent-input" value={colors.accent} onChange={(e) => setAccent(e.target.value)} className="w-14 h-14 cursor-pointer bg-transparent border-0 p-0" />
            <div>
              <p className="text-luxury-bone text-sm font-semibold">{t("اللون الذهبي المميز")}</p>
              <p className="text-luxury-gray text-xs" dir="ltr">{colors.accent}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 bg-luxury-black border border-luxury-border p-4 rounded-xl">
            <input type="color" data-testid="appearance-bg-input" value={colors.bg} onChange={(e) => setBg(e.target.value)} className="w-14 h-14 cursor-pointer bg-transparent border-0 p-0" />
            <div>
              <p className="text-luxury-bone text-sm font-semibold">{t("لون الخلفية الرئيسي")}</p>
              <p className="text-luxury-gray text-xs" dir="ltr">{colors.bg}</p>
            </div>
          </div>
        </div>
        {palette === "custom" && <p className="text-luxury-gold text-xs mt-4">— {t("مخصصة")}</p>}
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 mb-8">
        <h3 className="text-luxury-gold text-lg font-semibold mb-2">{t("ألوان الموقع — بالتفصيل")}</h3>
        <p className="text-luxury-gray text-xs mb-6">{t("كل لون موضح بمكانه بالضبط على الصفحة — غيّريه وافتحي الموقع لتراه")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {COLOR_FIELDS.map((f) => (
            <div key={f.key} data-testid={`appearance-color-${f.key}`} className="flex items-center gap-4 bg-luxury-black border border-luxury-border p-4 rounded-xl">
              <input
                type="color"
                data-testid={`appearance-color-input-${f.key}`}
                value={colors[f.key] || "#000000"}
                onChange={(e) => setColor(f.key, e.target.value)}
                className="w-14 h-14 shrink-0 cursor-pointer bg-transparent border-0 p-0"
              />
              <div className="min-w-0">
                <p className="text-luxury-bone text-sm font-semibold">{t(f.label)}</p>
                <p className="text-luxury-gray text-xs leading-relaxed mt-0.5">{t("يظهر في:")} {t(f.where)}</p>
                <p className="text-luxury-gold/70 text-[11px] mt-1" dir="ltr">{colors[f.key]}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 mb-8">
        <h3 className="text-luxury-gold text-lg font-semibold mb-2">{t("صور الصفحة")}</h3>
        <p className="text-luxury-gray text-xs mb-6">{t("كل صورة على الصفحة — ارفعي صورة جديدة أو ألصقي رابطاً")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {IMAGE_SLOTS.map((slot) => (
            <ImageField
              key={slot.key}
              testid={`appearance-image-${slot.key}`}
              label={`${t(slot.label)} — ${t(slot.hint)}`}
              value={images[slot.key]}
              onChange={(v) => setImages((s) => ({ ...s, [slot.key]: v }))}
            />
          ))}
          <ImageField
            testid="appearance-image-consultation"
            label={t("صورة قسم الاستشارات الخاصة — اختيارية (اتركيها فارغة لإخفاء الصورة)")}
            value={consultImage}
            onChange={setConsultImage}
          />
        </div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 mb-8">
        <h3 className="text-luxury-gold text-lg font-semibold mb-2">{t("خلفيات الأقسام — أضيفي صورة لأي قسم")}</h3>
        <p className="text-luxury-gray text-xs mb-6">{t("اختياري: صورة تظهر بشكل خافت وأنيق خلف محتوى القسم — اتركي الحقل فارغاً ليبقى القسم بلونه")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {SECTION_BG_SLOTS.map((slot) => (
            <ImageField
              key={slot.id}
              testid={`appearance-section-bg-${slot.id}`}
              label={t(slot.label)}
              value={sectionBg[slot.id] || ""}
              onChange={(v) => setSectionBg((s) => ({ ...s, [slot.id]: v }))}
            />
          ))}
        </div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 mb-8">
        <h3 className="text-luxury-gold text-lg font-semibold mb-2">{t("خطوط الموقع")}</h3>
        <p className="text-luxury-gray text-xs mb-6">{t("اختاري شخصية الخط — العناوين والنصوص تتغير معاً في كل الموقع")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {FONT_PAIRS.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`font-pair-${p.id}`}
              onClick={() => setFonts(p)}
              className={`relative rounded-2xl border p-6 text-start transition-all ${fonts.id === p.id ? "border-luxury-gold ring-1 ring-luxury-gold" : "border-luxury-border hover:border-luxury-gold/50"}`}
            >
              {fonts.id === p.id && (
                <span className="absolute top-4 end-4 w-6 h-6 rounded-full bg-luxury-gold flex items-center justify-center">
                  <Check className="w-4 h-4 text-luxury-black" strokeWidth={3} />
                </span>
              )}
              <p className="text-3xl text-luxury-bone mb-2" style={{ fontFamily: `'${p.serif}', serif` }}>الحب يبدأ من الداخل</p>
              <p className="text-sm text-luxury-gray" style={{ fontFamily: `'${p.sans}', sans-serif` }}>{t("نموذج نص بخط المحتوى — يتضح الفرق في القراءة الطويلة")}</p>
              <p className="text-luxury-gold text-xs mt-3 font-semibold">{t(p.name)}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-8 mb-8">
        <h3 className="text-luxury-gold text-lg font-semibold mb-6">{t("معاينة حية")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div data-testid="appearance-preview" className="rounded-2xl p-8 border border-luxury-border" style={{ background: colors.bg }}>
            <div className="rounded-xl p-6" style={{ background: colors.surface }}>
              <p className="text-2xl mb-1" style={{ color: colors.text, fontFamily: `'${fonts.serif}', serif` }}>TheMaya</p>
              <p className="text-sm mb-5" style={{ color: colors.muted, fontFamily: `'${fonts.sans}', sans-serif` }}>{t("هكذا تبدو البطاقات فوق الخلفية الداكنة")}</p>
              <span className="inline-block px-6 py-2.5 rounded-full text-sm font-semibold" style={{ background: colors.accent, color: colors.bg }}>{t("زر ذهبي")}</span>
              <span className="ms-4 text-sm font-semibold" style={{ color: colors.accent }}>{t("نص ذهبي")}</span>
            </div>
          </div>
          <div data-testid="appearance-preview-light" className="rounded-2xl p-8 border border-luxury-border" style={{ background: colors.light_bg }}>
            <div className="rounded-xl p-6 bg-white" style={{ border: `1px solid ${colors.light_border}` }}>
              <p className="text-2xl mb-1" style={{ color: colors.light_text, fontFamily: `'${fonts.serif}', serif` }}>من هي مايا؟</p>
              <p className="text-sm mb-5" style={{ color: colors.light_muted, fontFamily: `'${fonts.sans}', sans-serif` }}>{t("هكذا تبدو الأقسام الفاتحة")}</p>
              <span className="inline-block px-6 py-2.5 rounded-full text-sm font-semibold text-white" style={{ background: colors.bg }}>{t("زر داكن")}</span>
              <span className="ms-4 text-sm font-semibold" style={{ color: colors.accent }}>{t("نص ذهبي")}</span>
            </div>
          </div>
        </div>
      </section>

      <button data-testid="appearance-save-button" onClick={save} disabled={saving}
        className="bg-luxury-gold text-luxury-black font-semibold px-10 py-3.5 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-60 flex items-center gap-2">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
        {t("حفظ المظهر")}
      </button>
    </div>
  );
};
